const BILL_FIELDS = [
  "holderName", "holderTaxId", "supplyAddress", "postalAddress", "cups",
  "retailer", "distributor", "tariff", "contractedPowerPeriods", "consumptionPeriods",
] as const;

type ProposalInput = {
  documents: any[];
  supplies: any[];
  activeSupplyIndex: number;
  assignments?: Record<string, string>;
  edits?: Record<string, string>;
  alternativeSelections?: Record<string, number>;
  parties?: Record<string, any>;
  location?: Record<string, any>;
};

function candidateText(candidate: any) {
  return Array.isArray(candidate?.value) ? candidate.value.join("\n") : String(candidate?.value ?? "");
}

function resolvedProposal(candidate: any, field: string, edit: string | undefined, alternativeIndex: number | undefined) {
  const alternative = Number.isInteger(alternativeIndex) ? candidate?.alternatives?.[alternativeIndex!] : undefined;
  if (!candidate || ((candidate.value == null || candidate.reviewReason) && !alternative)) return undefined;
  if (edit !== undefined) return { value: edit, alternative };
  if (!alternative) return { value: candidateText(candidate), alternative: undefined };
  const isReading = field === "contractedPowerPeriods" || field === "consumptionPeriods";
  const value = isReading
    ? [...(candidate.normalized ?? []).map((row: any) => `${row.period}: ${row.value} ${row.unit}${row.startDate || row.endDate ? ` ${row.startDate ?? ""} - ${row.endDate ?? ""}` : ""}`), `${alternative.period ?? "P1"}: ${alternative.value} ${alternative.unit ?? (field === "contractedPowerPeriods" ? "kW" : "kWh")}${alternative.startDate || alternative.endDate ? ` ${alternative.startDate ?? ""} - ${alternative.endDate ?? ""}` : ""}`].join("\n")
    : String(alternative.value);
  return { value, alternative };
}

function hasExplicitPeriods(field: string, candidate: any, value: string) {
  if (Array.isArray(candidate?.normalized) && candidate.normalized.length) return true;
  const unit = field === "contractedPowerPeriods" ? "kW" : "kWh";
  return value.split(/\r?\n/).some(line => new RegExp(`^\\s*P[1-6]\\b.*\\d[\\d., ]*\\s*${unit}\\b`, "i").test(line));
}

function isOccupied(supply: any, index: number, parties: any, location: any, field: string) {
  if (field === "postalAddress") return Boolean(String(location?.billAddress ?? "").trim());
  const scalar: Record<string, string> = {
    holderName: "holderName", holderTaxId: "holderTaxId", supplyAddress: "address",
    cups: "cups", retailer: "retailer", distributor: "distributor", tariff: "tariff",
  };
  if (scalar[field]) {
    const legacy: Record<string, string> = {
      holderName: "supplyHolderName", holderTaxId: "supplyHolderTaxId", supplyAddress: "supplyAddress", cups: "supplyCups",
      retailer: "supplyRetailer", distributor: "supplyDistributor", tariff: "supplyTariff",
    };
    const value = supply?.[scalar[field]] ?? (index === 0 ? parties?.[legacy[field]] : undefined);
    return Boolean(String(value ?? "").trim());
  }
  if (field === "contractedPowerPeriods") return Boolean(supply?.contractedPower?.length || (index === 0 && String(parties?.power ?? "").trim()));
  if (field === "consumptionPeriods") return Boolean(supply?.consumptionPeriods?.length || (index === 0 && String(parties?.consumption ?? "").trim()));
  return true;
}

/** Build one deterministic, non-overwriting invoice batch from editable final-field proposals. */
export function buildCommercialBillProposalBatch(input: ProposalInput) {
  const supplies = input.supplies ?? [];
  const documents = [...(input.documents ?? [])]
    .filter(doc => doc.documentKind === "electricity_bill" && doc.extraction?.fields)
    .sort((a, b) => Number(a.displayOrder ?? a.id) - Number(b.displayOrder ?? b.id) || Number(a.id) - Number(b.id));
  const entries: Array<{ documentId: number; field: string; value: string; supplyId: string; alternativeIndex?: number }> = [];
  const unresolvedDocumentIds: number[] = [];
  const skippedOccupied: Array<{ documentId: number; field: string }> = [];
  const skippedInformational: Array<{ documentId: number; field: string }> = [];
  const scalarTargets = new Set<string>();

  for (const doc of documents) {
    const hasApplicableProposal = BILL_FIELDS.some(field => {
      const candidate = doc.extraction.fields[field];
      const key = `${doc.id}:${field}`;
      const selected = input.alternativeSelections?.[key];
      const resolved = resolvedProposal(candidate, field, input.edits?.[key], selected);
      if (!resolved || !resolved.value.trim()) return false;
      if (field === "contractedPowerPeriods" || field === "consumptionPeriods")
        return hasExplicitPeriods(field, candidate, resolved.value);
      return true;
    });
    if (!hasApplicableProposal) continue;
    const linkedGroupDoc = doc.captureGroupId && documents.find(sibling => sibling.captureGroupId === doc.captureGroupId && (sibling.extraction?.supplyId || input.assignments?.[String(sibling.id)]));
    const linkedGroup = linkedGroupDoc?.extraction?.supplyId ?? (linkedGroupDoc ? input.assignments?.[String(linkedGroupDoc.id)] : undefined);
    const supplyId = String(doc.extraction.supplyId ?? linkedGroup ?? input.assignments?.[String(doc.id)] ?? (supplies.length === 1 ? supplies[0]?.id : "") ?? "");
    const supplyIndex = supplies.findIndex(supply => String(supply.id) === supplyId);
    if (!supplyId || supplyIndex < 0) {
      unresolvedDocumentIds.push(Number(doc.id));
      continue;
    }
    const supply = supplies[supplyIndex] ?? {};
    for (const field of BILL_FIELDS) {
      const candidate = doc.extraction.fields[field];
      const key = `${doc.id}:${field}`;
      const selection = input.alternativeSelections?.[key];
      const resolved = resolvedProposal(candidate, field, input.edits?.[key], selection);
      if (!resolved) continue;
      const { value, alternative } = resolved;
      if (!value.trim()) continue;
      if (field === "contractedPowerPeriods" || field === "consumptionPeriods") {
        if (!hasExplicitPeriods(field, candidate, value)) {
          skippedInformational.push({ documentId: Number(doc.id), field });
          continue;
        }
      } else if (isOccupied(supply, supplyIndex, input.parties, input.location, field)) {
        skippedOccupied.push({ documentId: Number(doc.id), field });
        continue;
      }
      // Two bills may add distinct reading periods, but a scalar proposal may
      // only be applied once per supply. Keep its first source; never guess.
      if (field !== "contractedPowerPeriods" && field !== "consumptionPeriods") {
        const target = `${supplyId}:${field}`;
        if (scalarTargets.has(target)) continue;
        scalarTargets.add(target);
      }
      entries.push({ documentId: Number(doc.id), field, value, supplyId, ...(alternative ? { alternativeIndex: selection } : {}) });
    }
  }
  entries.sort((a, b) => a.documentId - b.documentId || a.field.localeCompare(b.field));
  return { entries, unresolvedDocumentIds, skippedOccupied, skippedInformational };
}

export function commercialBillCandidateValue(candidate: any, edit?: string) {
  if (edit != null) return edit;
  return candidateText(candidate);
}
